import {ReviewMediaType} from "../../definitions/types";
import {ReviewMediaReviewModel} from "./reviewMediaReviewModel";

export class ReviewMediaMiniModel {
    private readonly _id: number;
    private readonly _ratingId: number;
    private readonly _reviewId: number;
    private readonly _filename: string;
    private readonly _url: string;
    private readonly _width?: number;
    private readonly _height?: number;
    private readonly _visible: boolean;
    private readonly _created: Date;
    private readonly _updated?: Date;
    private readonly _index: number;

    constructor(reviewMediaType: {
        id: number
        ratingId: number
        reviewId: number
        filename: string
        url: string
        width?: number
        height?: number
        visible: boolean
        created: Date
        updated?: Date
    }, index?: number) {
        this._id = reviewMediaType.id;
        this._ratingId = reviewMediaType.ratingId;
        this._reviewId = reviewMediaType.reviewId;
        this._filename = reviewMediaType.filename;
        this._url = reviewMediaType.url;
        this._width = reviewMediaType.width;
        this._height = reviewMediaType.height;
        this._visible = reviewMediaType.visible;
        this._created = reviewMediaType.created;
        this._updated = reviewMediaType.updated;
        this._index = index ?? 0;
    }

    /**
     * @method id
     * @description Returns the id of the review media item.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method ratingId
     * @description Returns the rating id of the review media item.
     * @returns {number}
     */
    public get ratingId(): number {
        return this._ratingId;
    }

    /**
     * @method reviewId
     * @description Returns the review id of the review media item.
     * @returns {number}
     */
    public get reviewId(): number {
        return this._reviewId;
    }

    /**
     * @method filename
     * @description Returns the filename of the review media item.
     * @returns {string}
     */
    public get filename(): string {
        return this._filename;
    }

    /**
     * @method url
     * @description Returns the url of the review media item in AWS S3.
     * @returns {string}
     */
    public get url(): string {
        return this._url;
    }

    /**
     * @method width
     * @description Returns the width of the review media item.
     * @returns {number | undefined}
     */
    public get width(): number | undefined {
        return this._width;
    }

    /**
     * @method height
     * @description Returns the height of the review media item.
     * @returns {number | undefined}
     */
    public get height(): number | undefined {
        return this._height;
    }

    /**
     * @method visible
     * @description Returns the visibility of the review media item.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @method created
     * @description Returns the created date of the review media item in UTC.
     * @returns {Date}
     */
    public get created(): Date {
        return this._created;
    }

    /**
     * @method updated
     * @description Returns the updated date of the review media item in UTC.
     * @returns {Date | undefined}
     */
    public get updated(): Date | undefined {
        return this._updated;
    }

    /**
     * @method index
     * @description Returns the index of the review media item in the review media array.
     * @returns {number}
     */
    public get index(): number {
        return this._index;
    }

    /**
     * @method modalThumbnailStyle
     * @description Returns the style for the modal thumbnail of the review media item.
     * @param {number} activeId - The id of the active review media item.
     * @returns {{
     *     border: string,
     *     borderRadius: string
     * } | {}}
     */
    public modalThumbnailStyle(activeId: number): {} {
        return activeId === this?._id ? {
            border: "4px solid #FF69B4",
            borderRadius: "3px"
        } : {}
    }
}

export class ReviewMediaModel extends ReviewMediaMiniModel {
    private readonly _review: ReviewMediaReviewModel;

    constructor(reviewMediaType: ReviewMediaType) {
        super(reviewMediaType);
        this._review = new ReviewMediaReviewModel(reviewMediaType.review);
    }

    /**
     * @method review
     * @description Returns the review of the review media item.
     * @returns {ReviewMediaReviewModel}
     */
    public get review(): ReviewMediaReviewModel {
        return this._review;
    }
}